import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Link } from 'react-router';
import { openModal } from '../../actions/ModalActions';
import discountApi from './discountApi';
import ApplyDiscountModal from './ApplyDiscountModal';
import CancelDiscountModal from './CancelDiscountModal';
import { percent, benefitWindow, shortDate, money, labelClass } from '../../utils/discountFormat';
import { isFeatureDisabled } from '../../utils/featureFlags';

/**
 * Client page panel (OPE-112): effective discount today, the campaign
 * discount timeline, apply / redeem / cancel. Renders nothing without
 * VIEW_DISCOUNTS; mutations need MANAGE_DISCOUNTS (the API enforces too).
 * The manual client.discount slider stays in the side pane (MANAGE_CREDIT).
 *
 * Rollout is per target company and decided by the API, not by the staff
 * member's permissions: a 403 feature_disabled on the first request hides
 * the panel for that client, and apply/redeem are only offered while the
 * company is live (`campaigns_effective` on /effective_discounts).
 */
@connect(
    state => ({
        canView: state.auth.user.permissions.includes('VIEW_DISCOUNTS') || state.auth.user.permissions.includes('MANAGE_DISCOUNTS'),
        canManage: state.auth.user.permissions.includes('MANAGE_DISCOUNTS')
    }),
    dispatch => bindActionCreators({ openModal }, dispatch)
)
export default class ClientDiscountsPanel extends Component {
    constructor(props) {
        super(props);
        // usage: client_discount_id -> {loading|rows|error}, fetched on demand
        this.state = { discounts: null, effective: null, error: null, showAll: false, usage: {}, disabled: false };
    }

    componentDidMount() {
        if (this.props.canView) {
            this.load();
        }
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.client.id != this.props.client.id && nextProps.canView) {
            // a different company: its own rollout decision, nothing carried over
            this.setState({ discounts: null, effective: null, error: null, usage: {}, disabled: false });
            this.load(nextProps.client.id);
        }
    }

    load(clientId = this.props.client.id) {
        Promise.all([discountApi.listClientDiscounts(clientId), discountApi.effectiveDiscounts(clientId)])
            .then(([discounts, effective]) => {
                if (clientId == this.props.client.id) {
                    this.setState({ discounts, effective, error: null, usage: {}, disabled: false });
                }
            })
            .catch(error => {
                if (clientId != this.props.client.id) {
                    return;
                }
                if (isFeatureDisabled(error)) {
                    this.setState({ disabled: true, error: null });
                } else {
                    this.setState({ error: error.message });
                }
            });
    }

    setUsage(id, value) {
        this.setState({ usage: { ...this.state.usage, [id]: value } });
    }

    /** Which invoice lines did this discount actually come off? */
    toggleUsage(discount) {
        var id = discount.client_discount_id;
        var current = this.state.usage[id];
        if (current) {
            this.setUsage(id, current.open ? { ...current, open: false } : { ...current, open: true });
            return;
        }

        this.setUsage(id, { open: true, loading: true });
        discountApi.getClientDiscount(this.props.client.id, id)
            .then(full => this.setUsage(id, { open: true, rows: full.invoice_usage || [] }))
            .catch(error => this.setUsage(id, { open: true, error: error.message }));
    }

    renderUsage(discount) {
        var state = this.state.usage[discount.client_discount_id];
        if (!state || !state.open) {
            return null;
        }
        if (state.loading) {
            return <tr key={`${discount.client_discount_id}-usage`}><td colSpan={7} className="text-muted">Loading usage...</td></tr>;
        }
        if (state.error) {
            return <tr key={`${discount.client_discount_id}-usage`}><td colSpan={7}><span className="text-danger">{state.error}</span></td></tr>;
        }
        var rows = state.rows || [];
        if (rows.length === 0) {
            return <tr key={`${discount.client_discount_id}-usage`}><td colSpan={7} className="text-muted">Not used on any invoice yet.</td></tr>;
        }
        var total = rows.reduce((sum, u) => sum + Number(u.discount_amount || 0), 0);

        return (
            <tr key={`${discount.client_discount_id}-usage`}>
                <td colSpan={7} style={{ background: '#f9f9f9' }}>
                    <table className="table table-condensed" style={{ marginBottom: 0, background: 'transparent' }}>
                        <thead>
                            <tr><th>Period</th><th>User type</th><th className="text-right">Rate</th><th className="text-right">Discount</th></tr>
                        </thead>
                        <tbody>
                            {rows.map(u =>
                                <tr key={u.invoice_line_item_id}>
                                    <td>{shortDate(u.service_start)} – {shortDate(u.service_end)}</td>
                                    <td>{u.user_type || '–'}</td>
                                    <td className="text-right">{percent(u.percent_rate)}</td>
                                    <td className="text-right">{money(u.discount_amount)}</td>
                                </tr>
                            )}
                            <tr>
                                <td colSpan={3} className="text-right"><strong>Total</strong></td>
                                <td className="text-right"><strong>{money(total)}</strong></td>
                            </tr>
                        </tbody>
                    </table>
                </td>
            </tr>
        );
    }

    openApply() {
        this.props.openModal(ApplyDiscountModal, { client: this.props.client, onDone: () => this.load() });
    }

    openCancel(discount) {
        this.props.openModal(CancelDiscountModal, { clientId: this.props.client.id, discount, onDone: () => this.load() });
    }

    render() {
        if (!this.props.canView || this.state.disabled) {
            return null;
        }
        var { discounts, effective, error, showAll } = this.state;
        var live = !!(effective && effective.campaigns_effective);
        var visible = discounts ? discounts.filter(d => showAll || (d.phase !== 'cancelled' && d.phase !== 'expired')) : [];
        var hidden = discounts ? discounts.length - visible.length : 0;

        return (
            <div className="panel panel-default" style={{ marginBottom: 25 }}>
                <div className="panel-heading" style={{ display: 'flex', alignItems: 'center' }}>
                    <h2 style={{ flex: 1, margin: 0 }}><i className="fa fa-tag"></i> Campaign discounts</h2>
                    {effective &&
                        <span className="text-muted" style={{ marginRight: 15 }}>
                            effective today <strong>{percent(effective.effective_rate_today)}</strong>
                            {' '}(manual {percent(effective.manual_discount)}; highest single wins)
                        </span>
                    }
                    {effective && !live &&
                        <span className="label label-default" style={{ marginRight: 15 }} title="Campaign discounts are not billed for this company yet">not live</span>
                    }
                    {this.props.canManage && live &&
                        <button type="button" className="btn btn-sm btn-info" onClick={this.openApply.bind(this)}>
                            <i className="fa fa-plus-square"></i> Apply discount
                        </button>
                    }
                </div>
                <div className="panel-body no-padding">
                    {error && <div className="alert alert-danger" style={{ margin: 10 }}>{error}</div>}
                    {discounts === null && !error && <p className="text-muted" style={{ padding: 10 }}>Loading...</p>}
                    {discounts !== null && discounts.length === 0 && <p className="text-muted" style={{ padding: 10 }}>No campaign discounts on this client.</p>}
                    {visible.length > 0 &&
                        <table className="table table-condensed" style={{ marginBottom: 0 }}>
                            <thead>
                                <tr><th>Phase</th><th>Discount</th><th>Window</th><th>Promotion</th><th>Source</th><th>Applied</th><th></th></tr>
                            </thead>
                            <tbody>
                                {visible.map(d => [
                                    <tr key={d.client_discount_id}>
                                        <td><span className={labelClass(d.phase)}>{d.phase}</span></td>
                                        <td>{percent(d.percent_off)} <small className="text-muted">{d.scope}</small></td>
                                        <td>
                                            {benefitWindow(d.starts_on, d.ends_on)}
                                            {d.cancelled_at && <div className="text-muted"><small>cancelled {shortDate(d.cancelled_at)}: {d.cancellation_reason}</small></div>}
                                        </td>
                                        <td>
                                            {d.promotion ? <Link to={`/discounts/promotions/${d.promotion.promotion_id}`}>{d.promotion.name}</Link> : <span className="text-muted">–</span>}
                                            {d.coupon && <div className="text-muted"><small>{d.coupon.invoice_name}</small></div>}
                                            {d.promotion && d.promotion.code && <div><small><code>{d.promotion.code}</code></small></div>}
                                        </td>
                                        <td>{d.source_type}</td>
                                        <td>
                                            {shortDate(d.applied_at)}
                                            <div>
                                                <a href="#" onClick={e => { e.preventDefault(); this.toggleUsage(d); }}>
                                                    <small>{(this.state.usage[d.client_discount_id] || {}).open ? 'hide usage' : 'usage'}</small>
                                                </a>
                                            </div>
                                        </td>
                                        <td className="text-right">
                                            {this.props.canManage && live && !d.cancelled_at && d.phase !== 'expired' &&
                                                <button type="button" className="btn btn-xs btn-danger" onClick={() => this.openCancel(d)}>Cancel</button>
                                            }
                                        </td>
                                    </tr>,
                                    this.renderUsage(d)
                                ])}
                            </tbody>
                        </table>
                    }
                    {hidden > 0 && !showAll &&
                        <p style={{ padding: 10, margin: 0 }}><a href="#" onClick={e => { e.preventDefault(); this.setState({ showAll: true }); }}>Show {hidden} expired / cancelled</a></p>
                    }
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./app/components/discounts/ClientDiscountsPanel.js